import { Action } from '@reduxjs/toolkit';
import { isMediaInputDevice } from '@yandex-360/telemost-sdk/device-manager';
import { eventChannel, EventChannel } from 'redux-saga';
import { call, cancel, fork, race, select, take } from 'typed-redux-saga';

import { SELF } from '../../../../shared/constants';
import { audioLevelAnalyser, deviceManager, goloomSdk } from '../../../../shared/features/goloom-sdk';
import { getAudioContext } from '../../../../shared/helpers/audio-context';
import { ChannelHolder } from '../../../../shared/helpers/channelHolder';
import { hideUnmuteNotification, startAnalyze } from '../../helpers/talk-while-muted-helpers';
import { selectIsPermissionsExtendedEnabled } from '../../selectors';
import { cancelCallAction, updateParticipant } from '../conference/slice';
import { selectHasMicrophonePermission, updatePermissions } from '../permissions';

/**
 * Получение аудиотрека для анализа
 */
function getAudioTrack(): Maybe<MediaStreamTrack | null> {
    // Получаем устройство, стрим с него и затем ноду для анализатора
    const device = deviceManager.audioInputActiveDevice;

    if (device && isMediaInputDevice(device)) {
        return device.track;
    }
}

/**
 * Создание канала события, которое будет триггериться
 * каждый раз при получении данных с аудиотрека
 */
function createAudioAnalyzerChannel(): EventChannel<number> {
    const originalTrack = getAudioTrack();

    // Хак для FF и Safari, в которых мы получаем трек с enabled = false,
    // из-за чего анализатор не может получить громкость трека
    const track = originalTrack?.clone();
    if (track) {
        track.enabled = true;
    }

    const audioContext = getAudioContext();

    return eventChannel<number>((emit) => {
        if (!track || !audioContext || !audioLevelAnalyser) {
            return () => null;
        }

        const analyserCallback = (level: number) => {
            emit(level);
        };

        if (audioContext.state !== 'running') {
            audioContext.resume();
        }

        const stream = new MediaStream([track]);
        const sourceNode = audioContext.createMediaStreamSource(stream);

        audioLevelAnalyser.connect(sourceNode);
        audioLevelAnalyser.start({
            everyTickCallback: true,
            callback: analyserCallback,
        });

        return () => {
            track.stop();
            audioLevelAnalyser?.stop(analyserCallback);
            audioLevelAnalyser?.disconnect(sourceNode);
        };
    });
}

/**
 * Сага для запуска анализатора
 */
function* analyzerSaga() {
    const muteAudio = goloomSdk.session ? !goloomSdk.session.audioStatus : false;

    if (muteAudio) {
        const audioAnalyzerChannel = yield* call(createAudioAnalyzerChannel);
        yield* call(startAnalyze, audioAnalyzerChannel);
    } else {
        yield* call(hideUnmuteNotification);
    }
}

/**
 * Рутовая сага разговора с замьюченным микрофоном
 */
export function* rootTalkWhileMutedSaga() {
    if (NODE) {
        return;
    }

    const channelHolder = new ChannelHolder();

    try {
        let lastTask;

        while (true) {
            const { endCallAction } = yield* race({
                audioStatusChanged: channelHolder.take(deviceManager.onAudioStatusChanged),
                activeDeviceChanged: channelHolder.take(deviceManager.onActiveDevicesChanged),
                endCallAction: take(cancelCallAction),
                updatePermissionsAction: take(updatePermissions),
                updateParticipantAction: take(
                    (action: Action) => updateParticipant.match(action) && action.payload.id === SELF
                ),
            });

            if (lastTask) {
                yield* cancel(lastTask);
            }

            const isPermissionsExtendedEnabled = yield* select(selectIsPermissionsExtendedEnabled);
            const hasMicrophonePermission = yield* select(selectHasMicrophonePermission);
            if (!endCallAction && (!isPermissionsExtendedEnabled || hasMicrophonePermission)) {
                lastTask = yield* fork(analyzerSaga);
            }
        }
    } finally {
        channelHolder.close();
    }
}
